import React, { useState, useRef, useEffect } from 'react';
import api from '../lib/api';
import { Badge } from './ui/badge';
import { Loader2, ChevronDown, Search } from 'lucide-react';
import { toast } from 'sonner';

const BLOCKED_COMMANDS = [
  'rm -rf /', 'format c:', 'del /s /q c:\\',
  'mkfs', ':(){:|:&};:', 'dd if=/dev/zero of=/dev/sda',
  'shutdown -h now', 'halt', 'init 0',
];

const MAX_HISTORY = 50;

export default function TerminalEmulator({ deviceId, assetName, osType, scripts, runAs, onSessionLog }) {
  const [lines, setLines] = useState([
    { type: 'system', text: `CipherOne Terminal — ${assetName || 'Device'} (${osType === 'windows' ? 'PowerShell' : 'Bash'})` },
    { type: 'system', text: 'Select a script from the dropdown above, then type parameters and press Enter.' },
    { type: 'system', text: 'Type "help" for available commands, "clear" to reset.' },
    { type: 'divider' },
  ]);
  const [input, setInput] = useState('');
  const [executing, setExecuting] = useState(false);
  const [cmdHistoryList, setCmdHistoryList] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [sessionLog, setSessionLog] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [scriptDropdownOpen, setScriptDropdownOpen] = useState(false);
  const [scriptFilter, setScriptFilter] = useState('');
  const termRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const isWindows = (osType || '').toLowerCase().includes('windows') || (osType || '').toLowerCase().includes('win');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setScriptDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredScripts = (scripts || []).filter(s => {
    if (!scriptFilter) return true;
    const q = scriptFilter.toLowerCase();
    return (s.name || '').toLowerCase().includes(q) || (s.language || '').toLowerCase().includes(q);
  });

  useEffect(() => {
    scrollToBottom();
  }, [lines]);

  const scrollToBottom = () => {
    if (termRef.current) {
      termRef.current.scrollTop = termRef.current.scrollHeight;
    }
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const addLine = (type, text) => {
    setLines(prev => [...prev, { type, text, time: new Date().toISOString() }]);
  };

  const addSessionEntry = (cmd, output, status) => {
    const entry = { command: cmd, output, status, timestamp: new Date().toISOString() };
    setSessionLog(prev => {
      const next = [...prev, entry];
      if (onSessionLog) onSessionLog(next);
      return next;
    });
  };

  const handleBuiltinCommand = (cmd) => {
    const lower = cmd.trim().toLowerCase();
    if (lower === 'clear' || lower === 'cls') {
      setLines([{ type: 'system', text: 'Terminal cleared.' }, { type: 'divider' }]);
      return true;
    }
    if (lower === 'help') {
      addLine('output', [
        'Available commands:',
        '  help          — Show this help message',
        '  clear / cls   — Clear terminal',
        '  history       — Show command history',
        '  scripts       — List available NinjaRMM scripts',
        '  whoami        — Show current execution context',
        '',
        'Select a script from the dropdown above, then type parameters to pass to it.',
        'The typed text is sent as parameters to the selected NinjaRMM script.',
        isWindows ? 'Shell: PowerShell' : 'Shell: Bash',
      ].join('\n'));
      return true;
    }
    if (lower === 'history') {
      if (cmdHistoryList.length === 0) {
        addLine('output', 'No commands in history.');
      } else {
        addLine('output', cmdHistoryList.map((c, i) => `  ${i + 1}  ${c}`).join('\n'));
      }
      return true;
    }
    if (lower === 'scripts') {
      if (!scripts || scripts.length === 0) {
        addLine('output', 'No NinjaRMM scripts available.');
      } else {
        addLine('output', scripts.slice(0, 20).map(s => `  ${s.name || s.id} (${s.language || 'unknown'})`).join('\n'));
        if (scripts.length > 20) addLine('output', `  ... and ${scripts.length - 20} more`);
      }
      return true;
    }
    if (lower === 'whoami') {
      addLine('output', `Run as: ${runAs || 'SYSTEM'}\nDevice: ${assetName || deviceId}\nOS: ${osType || 'unknown'}\nScript: ${selectedScript ? selectedScript.name : '(none selected)'}`);
      return true;
    }
    return false;
  };

  const isBlocked = (cmd) => {
    const lower = cmd.trim().toLowerCase();
    return BLOCKED_COMMANDS.some(b => lower.includes(b));
  };

  const executeCommand = async (cmd) => {
    if (!cmd.trim()) return;

    // Add command to display
    addLine('command', cmd);

    // Update history
    setCmdHistoryList(prev => {
      const next = [cmd, ...prev.filter(c => c !== cmd)].slice(0, MAX_HISTORY);
      return next;
    });
    setHistoryIdx(-1);

    // Check builtins
    if (handleBuiltinCommand(cmd)) return;

    // Check blocked commands
    if (isBlocked(cmd)) {
      addLine('error', 'BLOCKED: This command is restricted for safety reasons.');
      addSessionEntry(cmd, 'BLOCKED', 'blocked');
      return;
    }

    // Ensure a script is explicitly selected
    if (!selectedScript) {
      addLine('error', 'No script selected. Use the dropdown above to choose a NinjaRMM script first.');
      addSessionEntry(cmd, 'No script selected', 'error');
      return;
    }

    setExecuting(true);
    addLine('system', `Running "${selectedScript.name}" with parameters...`);

    try {
      const isAction = selectedScript.type === 'ACTION';
      const payload = {
        script_type: isAction ? 'ACTION' : 'SCRIPT',
        script_content: cmd,
        run_as: runAs || 'SYSTEM',
        script_id: isAction ? undefined : selectedScript.id,
        script_uid: isAction ? selectedScript.uid : undefined,
        script_name: selectedScript.name,
      };

      const res = await api.post(`/ninja-remote/device/${deviceId}/run`, payload);
      const historyId = res.data?.history_id;
      if (!res.data?.success) {
        addLine('system', res.data?.message || 'Submission not confirmed. Review the action status.');
        addSessionEntry(cmd, res.data?.message || res.data?.status, res.data?.status || 'unverified');
        setExecuting(false); return;
      }

      if (!historyId) {
        addLine('output', res.data?.message || 'Command submitted (no tracking ID)');
        addSessionEntry(cmd, res.data?.message || 'Submitted', 'submitted');
        setExecuting(false);
        return;
      }

      // Poll for results
      let attempts = 0;
      const maxAttempts = 30;
      const pollInterval = 3000;

      const poll = async () => {
        try {
          const pollRes = await api.get(`/ninja-remote/history/${historyId}/poll-result`);
          const data = pollRes.data;

          if (data.completed) {
            if (data.output) {
              addLine('output', data.output);
            } else if (data.status === 'completed') {
              addLine('output', '(completed with no output)');
            } else {
              addLine('error', `Script ${data.status}: ${data.message || 'no details'}`);
            }
            addSessionEntry(cmd, data.output || data.message || data.status, data.status);
            setExecuting(false);
            return;
          }

          attempts++;
          if (attempts >= maxAttempts) {
            addLine('system', 'Polling timed out. The command may still be running on the device.');
            addSessionEntry(cmd, 'Polling timeout', 'timeout');
            setExecuting(false);
            return;
          }

          setTimeout(poll, pollInterval);
        } catch (err) {
          addLine('error', `Poll error: ${err.message}`);
          addSessionEntry(cmd, err.message, 'error');
          setExecuting(false);
        }
      };

      setTimeout(poll, pollInterval);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      addLine('error', `Error: ${detail}`);
      addSessionEntry(cmd, detail, 'error');
      if (detail.includes('OAuth') || detail.includes('authorized')) {
        addLine('system', 'Go to Integrations > NinjaRMM > Connect to authorize script execution.');
      }
      setExecuting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (executing || !input.trim()) return;
    const cmd = input;
    setInput('');
    executeCommand(cmd);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (cmdHistoryList.length === 0) return;
      const newIdx = Math.min(historyIdx + 1, cmdHistoryList.length - 1);
      setHistoryIdx(newIdx);
      setInput(cmdHistoryList[newIdx]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIdx <= 0) {
        setHistoryIdx(-1);
        setInput('');
      } else {
        const newIdx = historyIdx - 1;
        setHistoryIdx(newIdx);
        setInput(cmdHistoryList[newIdx]);
      }
    }
  };

  const prompt = isWindows ? 'PS>' : '$';

  return (
    <div
      className="rounded-lg overflow-hidden border border-gray-700 bg-[#1a1b26] font-mono text-sm shadow-lg"
      data-testid="terminal-emulator"
      onClick={focusInput}
    >
      {/* Title bar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#24283b] border-b border-gray-700">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-500 opacity-80" />
            <span className="w-3 h-3 rounded-full bg-yellow-500 opacity-80" />
            <span className="w-3 h-3 rounded-full bg-green-500 opacity-80" />
          </div>
          <span className="text-gray-400 text-xs ml-2">{assetName || 'Terminal'} — {isWindows ? 'PowerShell' : 'Bash'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] border-gray-600 text-gray-400">
            {runAs || 'SYSTEM'}
          </Badge>
          {executing && <Loader2 className="h-3 w-3 animate-spin text-cyan-400" />}
        </div>
      </div>

      {/* Script selector bar */}
      <div className="px-3 py-2 bg-[#1f2335] border-b border-gray-700 relative" ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-xs shrink-0">Script:</span>
          <button
            type="button"
            onClick={() => setScriptDropdownOpen(prev => !prev)}
            className="flex-1 flex items-center justify-between gap-2 px-2 py-1 rounded bg-[#292e42] border border-gray-600 hover:border-gray-500 transition-colors text-left min-w-0"
            data-testid="terminal-script-selector"
          >
            <span className={`text-xs truncate ${selectedScript ? 'text-cyan-300' : 'text-gray-500'}`}>
              {selectedScript ? `${selectedScript.name} (${selectedScript.language || selectedScript.type || 'script'})` : 'Select a script from NinjaRMM library...'}
            </span>
            <ChevronDown className={`h-3 w-3 text-gray-500 shrink-0 transition-transform ${scriptDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Dropdown */}
        {scriptDropdownOpen && (
          <div className="absolute left-2 right-2 top-full mt-1 bg-[#292e42] border border-gray-600 rounded-lg shadow-xl z-50 max-h-64 flex flex-col" data-testid="terminal-script-dropdown">
            <div className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-700">
              <Search className="h-3 w-3 text-gray-500" />
              <input
                type="text"
                value={scriptFilter}
                onChange={(e) => setScriptFilter(e.target.value)}
                placeholder="Filter scripts..."
                className="flex-1 bg-transparent text-xs text-gray-300 outline-none placeholder-gray-600"
                autoFocus
                data-testid="terminal-script-search"
              />
            </div>
            <div className="overflow-y-auto flex-1">
              {filteredScripts.length === 0 ? (
                <div className="px-3 py-3 text-xs text-gray-500 text-center">
                  {scripts?.length ? 'No scripts match your filter' : 'No scripts available. Load scripts first.'}
                </div>
              ) : (
                filteredScripts.map(s => (
                  <button
                    key={s.id || s.uid || s.name}
                    type="button"
                    onClick={() => {
                      setSelectedScript(s);
                      setScriptDropdownOpen(false);
                      setScriptFilter('');
                      addLine('system', `Script selected: "${s.name}"`);
                      inputRef.current?.focus();
                    }}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#1f2335] transition-colors flex items-center justify-between gap-2 ${
                      selectedScript?.id === s.id && selectedScript?.uid === s.uid ? 'bg-[#1f2335] text-cyan-300' : 'text-gray-300'
                    }`}
                    data-testid={`terminal-script-option-${s.id || s.uid}`}
                  >
                    <span className="truncate">{s.name || `Script #${s.id || s.uid}`}</span>
                    <span className="text-[10px] text-gray-500 shrink-0">{s.language || s.type || ''}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Terminal body */}
      <div
        ref={termRef}
        className="h-[420px] overflow-y-auto p-3 text-[13px] leading-relaxed scrollbar-thin"
        data-testid="terminal-output"
      >
        {lines.map((line, i) => {
          if (line.type === 'divider') {
            return <div key={i} className="border-b border-gray-700 my-2" />;
          }
          if (line.type === 'system') {
            return <div key={i} className="text-gray-500 text-xs">{line.text}</div>;
          }
          if (line.type === 'command') {
            return (
              <div key={i} className="flex gap-2 mt-1">
                <span className="text-cyan-400 select-none">{prompt}</span>
                <span className="text-gray-200">{line.text}</span>
              </div>
            );
          }
          if (line.type === 'error') {
            return <div key={i} className="text-red-400 whitespace-pre-wrap ml-4">{line.text}</div>;
          }
          return <div key={i} className="text-green-400 whitespace-pre-wrap ml-4">{line.text}</div>;
        })}
      </div>

      {/* Input line */}
      <form onSubmit={handleSubmit} className="flex items-center gap-2 px-3 py-2 bg-[#1a1b26] border-t border-gray-700">
        <span className="text-cyan-400 select-none">{prompt}</span>
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={executing}
          placeholder={executing ? 'Waiting for response...' : selectedScript ? `Parameters for "${selectedScript.name}"...` : 'Select a script above first...'}
          className="flex-1 bg-transparent text-gray-200 outline-none placeholder-gray-600 text-[13px]"
          autoFocus
          spellCheck={false}
          autoComplete="off"
          data-testid="terminal-input"
        />
      </form>
    </div>
  );
}
